/**
 * ReplayHUD — minimal playback controls that auto-hide.
 *
 * Play/pause, speed selector, timeline scrubber with chapter markers,
 * time display, and exit button.
 */

import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  speed: number;
  chapters: { t: number; type: string }[];
  onPlayPause: () => void;
  onSeek: (timeMs: number) => void;
  onSpeedChange: (speed: number) => void;
  onExit: () => void;
}

const SPEEDS = [0.5, 1, 2, 4];

function formatTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

export function ReplayHUD({
  isPlaying,
  currentTime,
  duration,
  speed,
  chapters,
  onPlayPause,
  onSeek,
  onSpeedChange,
  onExit,
}: Props) {
  const [visible, setVisible] = useState(true);
  const hideTimer = useRef<number>(0);
  const trackRef = useRef<HTMLDivElement>(null);

  // Auto-hide after 3s of no mouse movement
  useEffect(() => {
    const show = () => {
      setVisible(true);
      window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => setVisible(false), 3000);
    };

    window.addEventListener("mousemove", show);
    // Start the timer
    hideTimer.current = window.setTimeout(() => setVisible(false), 3000);

    return () => {
      window.removeEventListener("mousemove", show);
      window.clearTimeout(hideTimer.current);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === " " || e.key === "k") {
        e.preventDefault();
        onPlayPause();
      } else if (e.key === "ArrowLeft") {
        onSeek(Math.max(0, currentTime - 5000));
      } else if (e.key === "ArrowRight") {
        onSeek(Math.min(duration, currentTime + 5000));
      } else if (e.key === "Escape") {
        onExit();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onPlayPause, onSeek, onExit, currentTime, duration]);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      onSeek(pct * duration);
    },
    [onSeek, duration]
  );

  const cycleSpeed = useCallback(() => {
    const idx = SPEEDS.indexOf(speed);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    onSpeedChange(next);
  }, [speed, onSpeedChange]);

  const progress = duration > 0 ? currentTime / duration : 0;

  // Deduplicate chapter markers that are very close together
  const chapterMarkers = chapters.filter((c, i) => {
    if (i === 0) return true;
    return c.t - chapters[i - 1].t > 2000;
  });

  return (
    <div
      className="replay-hud"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 0.4s ease" }}
    >
      <div className="replay-hud-bar">
        {/* Play/pause */}
        <button className="replay-btn" onClick={onPlayPause} title={isPlaying ? "Pause (Space)" : "Play (Space)"}>
          {isPlaying ? "❚❚" : "▶"}
        </button>

        {/* Speed */}
        <button className="replay-btn replay-speed-btn" onClick={cycleSpeed} title="Playback speed">
          {speed}x
        </button>

        {/* Timeline */}
        <div className="replay-timeline" ref={trackRef} onClick={handleTrackClick}>
          <div className="replay-timeline-fill" style={{ width: `${progress * 100}%` }} />
          <div className="replay-timeline-head" style={{ left: `${progress * 100}%` }} />

          {/* Chapter markers */}
          {chapterMarkers.map((ch, i) => (
            <div
              key={i}
              className={`replay-chapter-marker replay-chapter-${ch.type}`}
              style={{ left: `${(ch.t / duration) * 100}%` }}
              title={ch.type}
            />
          ))}
        </div>

        {/* Time display */}
        <div className="replay-time">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        {/* Exit */}
        <button className="replay-btn replay-exit-btn" onClick={onExit} title="Exit (Esc)">
          ✕
        </button>
      </div>
    </div>
  );
}
